import { useKitRequestContext } from 'views/kitting/Kit/KitRequestContext';
import {
  canUnapproveKitRequest,
  canApproveKitRequest,
  customerManagesOwnWarehouse,
} from 'views/kitting/utils';
import {
  DialogContentText,
  Alert,
  AlertTitle,
  Box,
  ListItemIcon,
  Tooltip,
  Stack,
  MenuItem,
  Typography,
  ListSubheader,
} from '@mui/material';
import {
  useApproveKitRequestMutation,
  useUnapproveKitRequestMutation,
  useApproveAndStartKitRequestMutation,
  useUnapproveAndUnstartKitRequestMutation,
} from 'store/slices/clientV2/kitRequests';
import SidebarApproveButtonClientV2 from 'ui-component/DetailLayout/SidebarComponents/SidebarApproveButtonClientV2';
import {
  formatPrice,
  getDefaultShippingMethod,
  getPickupOptions,
  getShipmentMethod,
  getShipmentCarrierAndService,
} from 'utils/functions';
import { useIsCofactr } from 'hooks/useIsCofactr';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import { ALL_APP_IDS } from 'constants/appConstants';
import { useGetPriceModelQuery } from 'store/slices/apiV1/purchasing';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useGetOrgQuery } from 'store/slices/apiV1/org';
import { useSelector } from 'store';
import { useMemo, useEffect, useState } from 'react';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';
import * as Yup from 'yup';
import {
  formatToShortDate,
  convertDate,
  formatToUniversalDate,
} from 'utils/dates';
import { KitRequestSchema, KitRequestValidationType } from 'types/kitting';
import { useTypedForm } from 'hooks/useTypedForm';
import {
  HookFormDatePicker,
  HookFormSelect,
} from 'ui-component/HookFormComponents';
import { KitScrubbingAlert } from 'views/kitting/Kit/components/KitScrubbingAlert';
import KitScrubbingDialog from 'views/kitting/Kit/components/KitScrubbingDialog';
import _ from 'lodash';
import { IconPlaneDeparture } from '@tabler/icons-react';
import { ShippingMethods } from 'types/purchasing';
import { useWatch } from 'react-hook-form';

const formConstants = {
  targetShipDate: {
    id: 'targetShipDate' as const,
    label: 'Scheduled Ship Date',
  },
  shipMethod: {
    id: 'shipMethod' as const,
    label: 'Ship Method',
  },
};

export const approveUnapproveKitRequestValidationSchema = Yup.object({
  [formConstants.targetShipDate.id]: Yup.date().required(
    `${formConstants.targetShipDate.label} is required`
  ),
  [formConstants.shipMethod.id]: Yup.string().required(
    `${formConstants.shipMethod.label} is required`
  ),
});

export const ApproveUnapproveKitRequestDialog = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const tempKitEstimates = useFeature(FeatureFlags.tempKitEstimates).on;
  const tempStartKitWorkflow = useFeature(FeatureFlags.tempStartKitWorkflow).on;
  const {
    estimatedHandlingRequestData,
    kitRequest,
    estimatedShipDateData,
    decoratedKitRequestLines,
    isLoadingKitRequest,
    isLoadingKitRequestLines,
    validations,
  } = useKitRequestContext();
  const { isCofactr } = useIsCofactr();
  const { hasAppPermission } = useAppAccessContext();
  const { activeOrgId } = useSelector((state) => state.org);
  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);
  const defaultShippingMethod = getDefaultShippingMethod({ org });

  const dynamicShippingMethods = useMemo(() => {
    const pickupOptions = getPickupOptions(
      hasAppPermission(ALL_APP_IDS.WAREHOUSE)
    );

    return [...ShippingMethods, ...pickupOptions];
  }, [hasAppPermission]);

  const { data: priceModel } = useGetPriceModelQuery(
    org?.priceModel.id || skipToken
  );
  const estimatedCost = useMemo(
    () =>
      (estimatedHandlingRequestData?.numberOfReels ?? 0) *
      Number(priceModel?.handlingHandlingReel8 ?? 0),
    [estimatedHandlingRequestData, priceModel]
  );

  const hasValidationError = useMemo(
    () =>
      Object.values(validations.validations).some(
        (validation) =>
          validation.lines.length > 0 &&
          validation.validationType === KitRequestValidationType.ERROR
      ),
    [validations.validations]
  );

  const kitRequestShipMethod = getShipmentMethod(kitRequest);

  const methods = useTypedForm(approveUnapproveKitRequestValidationSchema, {
    defaultValues: {
      targetShipDate: estimatedShipDateData.estimatedShipDate
        ? convertDate(estimatedShipDateData.estimatedShipDate)
        : undefined,
      shipMethod: kitRequestShipMethod ?? defaultShippingMethod,
    },
  });

  const { control, formState, handleSubmit, getValues, reset } = methods;

  useEffect(() => {
    reset({
      targetShipDate: estimatedShipDateData.estimatedShipDate
        ? convertDate(estimatedShipDateData.estimatedShipDate)
        : undefined,
      shipMethod: kitRequestShipMethod ?? defaultShippingMethod,
    });
  }, [
    estimatedShipDateData.estimatedShipDate,
    kitRequestShipMethod,
    defaultShippingMethod,
    reset,
  ]);

  const renderShipDateMessage = useMemo(() => {
    if (!estimatedShipDateData.estimatedShipDate) return null;

    const formattedEstimatedDate = formatToShortDate(
      estimatedShipDateData.estimatedShipDate
    );
    const formattedExpectedByDate = estimatedShipDateData.latestExpectedStockLot
      ?.expectedBy
      ? formatToShortDate(
          estimatedShipDateData.latestExpectedStockLot.expectedBy
        )
      : null;

    if (kitRequest?.shipPartial) {
      if (formattedExpectedByDate) {
        return (
          <>
            Based on the required handling and processing time, initial
            shipments will begin by {formattedEstimatedDate}.
            <Box component="div" sx={{ mt: 0.5 }}>
              <em>
                Note: Final shipment will be completed after receiving remaining
                stock currently expected by {formattedExpectedByDate}
              </em>
            </Box>
          </>
        );
      }
      return (
        <>
          Based on the required handling and processing time, this kit will ship
          by {formattedEstimatedDate}.
        </>
      );
    }

    if (formattedExpectedByDate) {
      return (
        <>
          This kit is configured to ship complete (no partial shipments).{' '}
          <Box component="div" sx={{ mt: 0.5, fontWeight: 'bold' }}>
            Shipment is blocked until all stock is received, with final stock
            lot currently expected by {formattedExpectedByDate}.
          </Box>
        </>
      );
    }

    return (
      <>
        Based on the required handling and processing time, we estimate this
        complete kit will ship by {formattedEstimatedDate}.
      </>
    );
  }, [estimatedShipDateData, kitRequest?.shipPartial]);

  const getApprovePayload = () => {
    const { estimatedShipDate } = estimatedShipDateData;
    if (!estimatedShipDate) return undefined;

    const targetShipDate = getValues(formConstants.targetShipDate.id);
    const formattedEstimatedShipDate = formatToUniversalDate(
      convertDate(estimatedShipDate)
    );
    const formattedTargetShipDate = targetShipDate
      ? formatToUniversalDate(targetShipDate)
      : undefined;
    const shipMethod = getValues(formConstants.shipMethod.id);
    const { defaultShippingCarrier: carrier, defaultShippingService: service } =
      shipMethod
        ? getShipmentCarrierAndService(shipMethod)
        : {
            defaultShippingCarrier: undefined,
            defaultShippingService: undefined,
          };
    return {
      estimatedShipDate,
      ...(targetShipDate &&
        formattedTargetShipDate !== formattedEstimatedShipDate && {
          targetShipDate: formattedTargetShipDate,
        }),
      ...(carrier &&
        service && {
          carrier,
          service,
        }),
    };
  };

  const watchShipMethod = useWatch({
    control,
    name: formConstants.shipMethod.id,
  });

  const canApprove = kitRequest
    ? canApproveKitRequest(
        kitRequest,
        (decoratedKitRequestLines ?? []).length > 0
      )
    : false;
  const canUnapprove = kitRequest
    ? canUnapproveKitRequest(kitRequest, isCofactr)
    : false;

  const shouldUseCombinedEndpoint =
    tempStartKitWorkflow &&
    kitRequest &&
    customerManagesOwnWarehouse(kitRequest);

  const sharedDialogProps = {
    idToUpdate: kitRequest?.id ?? '',
    recordName: 'Kit' as const,
    approved: !!kitRequest?.approved,
    disabled:
      isLoadingKitRequest ||
      isLoadingKitRequestLines ||
      (kitRequest?.approved ? !canUnapprove : !canApprove),
    tooltip:
      !!kitRequest?.approved && !isCofactr
        ? 'If you need to unapprove this kit request, please contact Cofactr Support using the chat icon in the bottom right corner.'
        : !kitRequest?.approved && hasValidationError
        ? 'This kit cannot be approved until all validation errors have been resolved.'
        : undefined,
    getApprovePayload,
  };

  const dialogPropsConfig = {
    maxWidth: kitRequest?.approved ? ('sm' as const) : ('md' as const),
    confirmSubmit:
      !kitRequest?.approved && (!!hasValidationError || !watchShipMethod),
    confirmSubmitBody: (
      <Stack spacing={2}>
        {hasValidationError && (
          <Alert severity="error">
            <AlertTitle>Kit Validation Errors</AlertTitle>
            Some parts in this kit request cannot be shipped because they are
            currently unavailable in your inventory. Are you sure you want to
            proceed with approval?
          </Alert>
        )}
        {!watchShipMethod && (
          <Alert severity="error">
            <AlertTitle>Shipping Method Missing</AlertTitle>
            Your kit won&apos;t ship until a shipping method is selected. Are
            you sure you want to proceed?
          </Alert>
        )}
      </Stack>
    ),
  };

  const additionalContent = !kitRequest?.approved && (
    <Box>
      <form onSubmit={handleSubmit(getApprovePayload)}>
        <DialogContentText>
          The cost to complete this kit is estimated to be{' '}
          {formatPrice(estimatedCost)}.{' '}
        </DialogContentText>
        <DialogContentText>
          <em>(This price is an estimate of the cost)</em>
        </DialogContentText>
        {estimatedShipDateData.estimatedShipDate && (
          <>
            {kitRequest &&
              (validations.isLoading ||
                _.some(
                  Object.values(validations.validations),
                  (validation) => validation && validation.lines.length > 0
                )) && (
                <KitScrubbingAlert
                  isLoading={validations.isLoading}
                  validations={validations.validations}
                  sx={{ my: 3 }}
                  onClick={() => setDialogOpen(true)}
                />
              )}
            {tempKitEstimates && (
              <>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <AlertTitle>Estimated Ship Date</AlertTitle>
                  {renderShipDateMessage}
                </Alert>
                <HookFormDatePicker
                  name={formConstants.targetShipDate.id}
                  label={formConstants.targetShipDate.label}
                  control={control}
                  errors={formState.errors}
                  minDate={
                    estimatedShipDateData.estimatedShipDate
                      ? convertDate(estimatedShipDateData.estimatedShipDate)
                      : undefined
                  }
                />
              </>
            )}
            <HookFormSelect
              control={control}
              name={formConstants.shipMethod.id}
              label={formConstants.shipMethod.label}
              errors={formState.errors}
              mt={0}
            >
              {dynamicShippingMethods.map((method) =>
                method.type === 'option' ? (
                  <MenuItem value={method.value} key={method.value}>
                    <Stack
                      direction="row"
                      justifyContent="flex-start"
                      alignItems="center"
                    >
                      {method.value === defaultShippingMethod && (
                        <Tooltip title="The default shipping method for your organization">
                          <ListItemIcon>
                            <IconPlaneDeparture />
                          </ListItemIcon>
                        </Tooltip>
                      )}
                      <Typography>{method.label}</Typography>
                    </Stack>
                  </MenuItem>
                ) : (
                  <ListSubheader key={method.value}>
                    {method.label}
                  </ListSubheader>
                )
              )}
            </HookFormSelect>
          </>
        )}
      </form>
    </Box>
  );

  return kitRequest ? (
    <>
      {shouldUseCombinedEndpoint ? (
        <SidebarApproveButtonClientV2<
          KitRequestSchema,
          typeof useApproveAndStartKitRequestMutation,
          typeof useUnapproveAndUnstartKitRequestMutation
        >
          {...sharedDialogProps}
          dialogProps={dialogPropsConfig}
          additionalContent={additionalContent}
          useClientApproveMutation={useApproveAndStartKitRequestMutation}
          useClientUnapproveMutation={useUnapproveAndUnstartKitRequestMutation}
        />
      ) : (
        <SidebarApproveButtonClientV2<
          KitRequestSchema,
          typeof useApproveKitRequestMutation,
          typeof useUnapproveKitRequestMutation
        >
          {...sharedDialogProps}
          dialogProps={dialogPropsConfig}
          additionalContent={additionalContent}
          useClientApproveMutation={useApproveKitRequestMutation}
          useClientUnapproveMutation={useUnapproveKitRequestMutation}
        />
      )}
      <KitScrubbingDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  ) : null;
};
